import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../../../../MainURL';
import Loading from '../../../../components/Loading';
import { useAtom } from 'jotai';
import { userDataAtom } from '../../../../AtomStore';
import { scoutApi, errorMessage } from '../../applications/applicationStatusApi';

interface ResumeProps {
  id: string;
  title: string;
  updatedAt: string;
  isPublic: boolean;
}

export default function ResumeManagePanel() {
  let navigate = useNavigate();
  const [userData] = useAtom(userDataAtom);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [resumeList, setResumeList] = useState<ResumeProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // 이력서별 교회 스카우트 제안 수신 동의 (인재 검색 노출 여부)
  const [scoutOptIns, setScoutOptIns] = useState<Record<string, boolean>>({});

  useEffect(() => {
    scoutApi
      .optIns()
      .then((items) => setScoutOptIns(Object.fromEntries(items.map((i) => [String(i.resumeId), i.scoutOptIn]))))
      .catch(() => setScoutOptIns({}));
  }, [refresh]);

  const toggleScout = async (resumeId: string) => {
    const next = !scoutOptIns[resumeId];
    if (next && !window.confirm('이 이력서를 교회의 인재 검색에 노출할까요?\n이름은 가려지고, 연락처는 교회가 연락한 뒤에만 공개됩니다.')) return;
    setScoutOptIns((prev) => ({ ...prev, [resumeId]: next }));
    try {
      await scoutApi.setOptIn(resumeId, next);
    } catch (error) {
      setScoutOptIns((prev) => ({ ...prev, [resumeId]: !next }));
      alert(errorMessage(error, '설정을 저장하지 못했습니다.'));
    }
  };

  // 이력서 목록 가져오기
  const fetchResumeList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${MainURL}/resume/getresumelist/${userData.userAccount}`);
      if (res.data.success && Array.isArray(res.data.resumeList)) {
        setResumeList(res.data.resumeList);
      } else {
        setResumeList([]);
      }
    } catch (error) {
      setResumeList([]);
      console.error('이력서 목록 가져오기 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResumeList();
  }, [refresh]);

  // 이력서 삭제 함수
  const handleDeleteResume = async (resumeId: string) => {
    if (window.confirm('정말로 이 이력서를 삭제하시겠습니까?')) {
      try {
        const res = await axios.post(`${MainURL}/resume/deleteresume`, {
          resumeId: resumeId,
          userAccount: userData.userAccount
        });
        if (res.data.success) {
          alert('이력서가 삭제되었습니다.');
          setRefresh(!refresh);
        } else {
          alert(res.data.error || '삭제에 실패했습니다.');
        }
      } catch (error) {
        alert((axios.isAxiosError(error) && error.response?.data?.error) || '삭제에 실패했습니다.');
        console.error('삭제 실패:', error);
      }
    }
  };

  // 이력서 복사 함수
  const handleCopyResume = async (resumeId: string) => {
    if (window.confirm('이 이력서를 복사하시겠습니까?\n복사본은 비공개로 생성됩니다.')) {
      try {
        const res = await axios.post(`${MainURL}/resume/copyresume`, {
          resumeId: resumeId,
          userAccount: userData.userAccount
        });
        if (res.data.success) {
          alert('이력서가 복사되었습니다.');
          setRefresh(!refresh);
        } else {
          alert(res.data.error || '복사에 실패했습니다.');
        }
      } catch (error) {
        alert((axios.isAxiosError(error) && error.response?.data?.error) || '복사에 실패했습니다.');
        console.error('복사 실패:', error);
      }
    }
  };

  // 이력서 보기 페이지로 이동
  const handleViewResume = (resumeId: string) => {
    navigate(`/mypage/resumeview?id=${resumeId}`);
    window.scrollTo(0, 0);
  };

  // 이력서 보기 페이지 링크 복사
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  const handleCopyLink = async (resumeId: string) => {
    const url = `${window.location.origin}/mypage/resumeview?id=${resumeId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // clipboard API 미지원(구형 브라우저/웹뷰) 폴백
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedLinkId(resumeId);
  };

  // 이력서 수정 페이지로 이동
  const handleEditResume = (resumeId: string) => {
    navigate(`/mypage/resumeedit?id=${resumeId}`);
    window.scrollTo(0, 0);
  };

  // 이력서 작성 페이지로 이동
  const handleCreateResume = () => {
    navigate('/mypage/resumeedit');
    window.scrollTo(0, 0);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="mypage-btn mypage-btn--primary" onClick={handleCreateResume}>
          이력서 작성
        </button>
      </div>

      <div className="subpage__main__content" style={{ marginTop: 18 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
            <Loading />
          </div>
        ) : Array.isArray(resumeList) && resumeList.length > 0 ? (
          <div className="postingList">
            {resumeList.map((resume) => (
              <div key={resume.id} className="postingItem">
                <div className="postingHeader">
                  <div className="postingTitle">
                    <h3>{resume.title || '제목 없음'}</h3>
                    <span className="postingDate">{resume.isPublic ? '사역자정보 공개' : '비공개'}</span>
                    <span className="postingDate">{resume.updatedAt}</span>
                    <label className="postingDate mm-check" style={{ marginTop: 6 }}>
                      <input className="checkbox" type="checkbox" checked={Boolean(scoutOptIns[resume.id])} onChange={() => toggleScout(resume.id)} />
                      <span className="checkmark" />
                      교회 스카우트 제안 받기
                    </label>
                  </div>
                  <div className="postingActions">
                    <button
                      className="actionBtn editBtn"
                      onClick={() => handleViewResume(resume.id)}
                    >
                      보기
                    </button>
                    <button
                      className="actionBtn editBtn"
                      onClick={() => handleCopyLink(resume.id)}
                    >
                      {copiedLinkId === resume.id ? '복사됨' : '링크복사'}
                    </button>
                    <button
                      className="actionBtn editBtn"
                      onClick={() => handleEditResume(resume.id)}
                    >
                      수정
                    </button>
                    <button
                      className="actionBtn editBtn"
                      onClick={() => handleCopyResume(resume.id)}
                    >
                      복사
                    </button>
                    <button
                      className="actionBtn deleteBtn"
                      onClick={() => handleDeleteResume(resume.id)}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="noPosts">
            <p>작성한 이력서가 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
}
